import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaArrowRight, 
  FaCogs, 
  FaCheck, 
  FaRocket, 
  FaSync, 
  FaShieldAlt,
  FaChartLine,
  FaServer,
  FaCode,
  FaDatabase,
  FaCloud,
  FaClock,
  FaUsers,
  FaStar,
  FaQuoteLeft,
  FaInfinity,
  FaTools,
  FaEye
} from 'react-icons/fa';
import { SiDocker, SiKubernetes, SiJenkins, SiGitlab, SiTerraform, SiAnsible, SiGithubactions, SiPrometheus } from 'react-icons/si';
import { serviceSeoData, seoImage } from '../../config/pageSeoConfig';
import './DevOps.scss';

const DevOps: React.FC = () => {
  // SEO constants from config
  const seoTitle = serviceSeoData.devops.title;
  const seoDescription = serviceSeoData.devops.description;
  const seoKeywords = serviceSeoData.devops.keywords;
  const seoUrl = serviceSeoData.devops.url;
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "DevOps Services",
    "description": seoDescription,
    "provider": {
      "@type": "Organization",
      "name": "App Developer Pro",
      "url": "https://appdeveloperpro.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://appdeveloperpro.com/images/team-at-work-app-developer-pro.jpg"
      }
    },
    "areaServed": "Worldwide",
    "serviceType": "DevOps, CI/CD, Automation, Cloud Infrastructure, Docker, Kubernetes, Monitoring, Security, Infrastructure as Code, 2025",
    "url": seoUrl,
    "image": seoImage
  };
  const features = [
    {
      icon: FaInfinity,
      title: 'CI/CD Pipelines',
      description: 'Automated build, test, and deployment pipelines for faster releases'
    },
    {
      icon: SiDocker,
      title: 'Containerization',
      description: 'Docker and Kubernetes for consistent deployments across environments'
    },
    {
      icon: FaEye,
      title: 'Monitoring & Logging',
      description: 'Comprehensive monitoring and alerting for system health and performance'
    },
    {
      icon: FaTools,
      title: 'Infrastructure as Code',
      description: 'Version-controlled infrastructure using Terraform and Ansible'
    },
    {
      icon: FaSync,
      title: 'Automated Testing',
      description: 'Automated unit, integration, and security testing in the pipeline'
    },
    {
      icon: FaShieldAlt,
      title: 'Security Integration',
      description: 'DevSecOps practices with security built into the development process'
    }
  ];

  const tools = [
    { icon: SiJenkins, name: 'Jenkins', color: '#D33833' },
    { icon: SiGitlab, name: 'GitLab CI', color: '#FC6D26' },
    { icon: SiDocker, name: 'Docker', color: '#2496ED' },
    { icon: SiKubernetes, name: 'Kubernetes', color: '#326CE5' },
    { icon: SiTerraform, name: 'Terraform', color: '#623CE4' },
    { icon: SiAnsible, name: 'Ansible', color: '#EE0000' },
    { icon: FaCloud, name: 'AWS/Azure', color: '#FF9900' },
    { icon: FaChartLine, name: 'Monitoring', color: '#FF6B35' },
    { icon: SiGithubactions, name: 'GitHub Actions', color: '#2088FF' },
    { icon: SiPrometheus, name: 'Prometheus', color: '#E6522C' }
  ];

  const benefits = [
    {
      icon: FaRocket,
      title: 'Faster Deployments',
      description: 'Reduce deployment time from hours to minutes with automated pipelines'
    },
    {
      icon: FaShieldAlt,
      title: 'Improved Reliability',
      description: 'Consistent deployments and reduced human error through automation'
    },
    {
      icon: FaSync,
      title: 'Continuous Improvement',
      description: 'Rapid feedback loops and iterative improvements to development process'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Assessment & Strategy',
      description: 'Evaluate current processes and design DevOps transformation roadmap'
    },
    {
      step: '02',
      title: 'Tool Implementation',
      description: 'Set up CI/CD pipelines, monitoring, and infrastructure automation'
    },
    {
      step: '03',
      title: 'Team Training',
      description: 'Train development teams on DevOps practices and tools'
    },
    {
      step: '04',
      title: 'Continuous Optimization',
      description: 'Monitor performance and continuously improve processes'
    }
  ];

  const testimonials = [
    {
      text: "Our deployment frequency increased by 10x and our lead time decreased by 80%. The DevOps transformation was incredible.",
      author: "Alex Thompson",
      company: "Software Company",
      rating: 5
    },
    {
      text: "The automated testing and monitoring they implemented caught issues before they reached production. Game changer!",
      author: "Priya Patel",
      company: "Startup",
      rating: 5
    }
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content="devops, ci/cd, automation, cloud infrastructure, docker, kubernetes, monitoring, security, infrastructure as code, 2025" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoUrl} />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content="App Developer Pro" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <meta name="twitter:site" content="@appdeveloperpro" />
        {/* Schema.org Markup */}
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <div className="devops">
      {/* Hero Section */}
      <section className="devops__hero">
        <div className="devops__hero-content">
          <motion.div
            className="devops__hero-text"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="devops__hero-title">
              Modern <span className="devops__hero-highlight">DevOps</span> & Automation
            </h1>
            <p className="devops__hero-subtitle">
              Accelerate your development with automated CI/CD pipelines, infrastructure as code, and comprehensive monitoring. Transform your software delivery process for faster, more reliable deployments.
            </p>
            <div className="devops__hero-buttons">
              <Link to="/contact" className="devops__hero-btn devops__hero-btn--primary">
                Start DevOps Journey <FaArrowRight />
              </Link>
              <Link to="/portfolio" className="devops__hero-btn devops__hero-btn--secondary">
                View Case Studies
              </Link>
            </div>
          </motion.div>
          <motion.div
            className="devops__hero-visual"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="devops__hero-pipeline">
              <div className="devops__hero-pipeline-header">
                <div className="devops__hero-pipeline-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span>CI/CD Pipeline</span>
              </div>
              <div className="devops__hero-pipeline-content">
                <div className="devops__hero-pipeline-stages">
                  <div className="devops__hero-pipeline-stage">
                    <div className="devops__hero-pipeline-stage-icon">
                      <FaCode />
                    </div>
                    <div className="devops__hero-pipeline-stage-info">
                      <div className="devops__hero-pipeline-stage-name">Build</div>
                      <div className="devops__hero-pipeline-stage-status success">Passed</div>
                    </div>
                  </div>
                  <div className="devops__hero-pipeline-stage">
                    <div className="devops__hero-pipeline-stage-icon">
                      <FaCheck />
                    </div>
                    <div className="devops__hero-pipeline-stage-info">
                      <div className="devops__hero-pipeline-stage-name">Test</div>
                      <div className="devops__hero-pipeline-stage-status success">Passed</div>
                    </div>
                  </div>
                  <div className="devops__hero-pipeline-stage">
                    <div className="devops__hero-pipeline-stage-icon">
                      <FaRocket />
                    </div>
                    <div className="devops__hero-pipeline-stage-info">
                      <div className="devops__hero-pipeline-stage-name">Deploy</div>
                      <div className="devops__hero-pipeline-stage-status running">Running</div>
                    </div>
                  </div>
                </div>
                <div className="devops__hero-pipeline-metrics">
                  <div className="devops__hero-pipeline-metric">
                    <div className="devops__hero-pipeline-metric-value">2.5 min</div>
                    <div className="devops__hero-pipeline-metric-label">Build Time</div>
                  </div>
                  <div className="devops__hero-pipeline-metric">
                    <div className="devops__hero-pipeline-metric-value">98.5%</div>
                    <div className="devops__hero-pipeline-metric-label">Success Rate</div>
                  </div>
                </div>
                <div className="devops__hero-pipeline-tools">
                  <motion.div 
                    className="devops__hero-pipeline-tool"
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  >
                    <FaCogs />
                  </motion.div>
                  <motion.div 
                    className="devops__hero-pipeline-tool"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <SiDocker />
                  </motion.div>
                  <motion.div 
                    className="devops__hero-pipeline-tool"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <SiKubernetes />
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="devops__features">
        <div className="devops__section-header">
          <motion.h2
            className="devops__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Our DevOps Services
          </motion.h2>
          <motion.p
            className="devops__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Comprehensive DevOps solutions for modern software development
          </motion.p>
        </div>
        <div className="devops__features-grid">
          {features.map((feature, index) => {
            const FeatureIcon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                className="devops__feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="devops__feature-header">
                  <div className="devops__feature-icon">
                    <FeatureIcon />
                  </div>
                  <h3 className="devops__feature-title">{feature.title}</h3>
                </div>
                <p className="devops__feature-description">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Tools Section */}
      <section className="devops__tools">
        <motion.div
          className="devops__section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="devops__section-title">DevOps Tools & Technologies</h2>
          <p className="devops__section-subtitle">
            Industry-leading tools for complete DevOps automation
          </p>
        </motion.div>
        <div className="devops__tools-grid">
          {tools.map((tool, index) => {
            const ToolIcon = tool.icon;
            return (
              <motion.div
                key={tool.name}
                className="devops__tool-item"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.1, y: -5 }}
              >
                <div 
                  className="devops__tool-icon"
                  style={{ '--tool-color': tool.color } as React.CSSProperties}
                >
                  <ToolIcon />
                </div>
                <span className="devops__tool-name">{tool.name}</span>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="devops__benefits">
        <div className="devops__section-header">
          <motion.h2
            className="devops__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Why Choose Our DevOps Services
          </motion.h2>
        </div>
        <div className="devops__benefits-grid">
          {benefits.map((benefit, index) => {
            const BenefitIcon = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                className="devops__benefit-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="devops__benefit-icon">
                  <BenefitIcon />
                </div>
                <h3 className="devops__benefit-title">{benefit.title}</h3>
                <p className="devops__benefit-description">{benefit.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Process Section */}
      <section className="devops__process">
        <div className="devops__section-header">
          <motion.h2
            className="devops__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Our DevOps Implementation Process
          </motion.h2>
          <motion.p
            className="devops__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            A structured approach to DevOps transformation
          </motion.p>
        </div>
        <div className="devops__process-timeline">
          {process.map((step, index) => (
            <motion.div
              key={step.step}
              className="devops__process-step"
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <div className="devops__process-number">{step.step}</div>
              <div className="devops__process-content">
                <h3 className="devops__process-title">{step.title}</h3>
                <p className="devops__process-description">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

     

      {/* FAQ Section - SEO Boost */}
      <section className="devops__faq">
        <div className="container">
          <motion.div
            className="devops__section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Frequently Asked Questions About DevOps Services</h2>
            <p>Everything you need to know about DevOps and infrastructure automation</p>
          </motion.div>
          <div className="devops__faq-list">
            <motion.div
              className="devops__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>What is DevOps and why does my business need it?</h3>
              <p>DevOps combines development and operations to accelerate software delivery, improve quality, and reduce costs. It automates deployment, testing, and infrastructure management. Benefits include faster releases (10x+), 60% fewer failures, 90% faster recovery times, and improved collaboration between teams. It's essential for competitive modern businesses.</p>
            </motion.div>
            <motion.div
              className="devops__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3>How much does DevOps implementation cost?</h3>
              <p>DevOps costs vary by scope. Basic CI/CD setup starts at $1,000-$3,000, comprehensive DevOps transformation costs $3,000-$6,000, and enterprise-level implementations range from $6,000-$10,000+. Ongoing managed services typically cost $300-$500+/month depending on infrastructure complexity and support level.</p>
            </motion.div>
            <motion.div
              className="devops__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>What tools do you use for DevOps?</h3>
              <p>We use industry-leading tools including Docker and Kubernetes for containerization, Jenkins/GitLab CI/CD for automation, Terraform for infrastructure as code, Ansible for configuration management, Prometheus/Grafana for monitoring, and AWS/Azure/GCP for cloud infrastructure. We select the best tools for your specific needs.</p>
            </motion.div>
            <motion.div
              className="devops__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3>How long does DevOps implementation take?</h3>
              <p>Implementation timelines vary: Basic CI/CD pipeline setup takes 2-3 weeks, comprehensive DevOps transformation takes 3-4 weeks, and enterprise-wide adoption takes 4-8 weeks. We use phased approaches to deliver quick wins early while building toward full automation and continuous improvement culture.</p>
            </motion.div>
            <motion.div
              className="devops__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3>Can you migrate our existing infrastructure to DevOps?</h3>
              <p>Yes! We specialize in migrating legacy infrastructure to modern DevOps practices. We assess your current setup, create a migration roadmap, implement automation gradually, train your team, and ensure zero downtime during transition. Most migrations complete within 3-6 months with immediate productivity improvements.</p>
            </motion.div>
            <motion.div
              className="devops__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>Do you provide 24/7 DevOps support and monitoring?</h3>
              <p>Absolutely! We offer comprehensive managed DevOps services including 24/7 monitoring, incident response, security patching, performance optimization, backup management, and infrastructure scaling. Our team ensures your systems run smoothly with 99.9%+ uptime guarantees and rapid response to any issues.</p>
            </motion.div>
          </div>
        </div>
      </section>
       {/* Testimonials Section */}
      <section className="devops__testimonials">
        <div className="devops__section-header">
          <motion.h2
            className="devops__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Client Success Stories
          </motion.h2>
        </div>
        <div className="devops__testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.author}
              className="devops__testimonial-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="devops__testimonial-quote">
                <FaQuoteLeft />
              </div>
              <div className="devops__testimonial-avatar">
                <span>{testimonial.author.charAt(0)}</span>
              </div>
              <div className="devops__testimonial-stars">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>
              <p className="devops__testimonial-text">{testimonial.text}</p>
              <div className="devops__testimonial-author">
                <strong>{testimonial.author}</strong>
                <span>{testimonial.company}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="devops__cta">
        <motion.div
          className="devops__cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="devops__cta-title">Ready to Transform Your Development Process?</h2>
          <p className="devops__cta-text">
            Get a free DevOps assessment and discover how automation can accelerate your software delivery and improve reliability.
          </p>
          <div className="devops__cta-buttons">
            <Link to="/contact" className="devops__cta-btn devops__cta-btn--primary">
              Get Free Assessment <FaArrowRight />
            </Link>
            <Link to="/portfolio" className="devops__cta-btn devops__cta-btn--secondary">
              View Case Studies
            </Link>
          </div>
        </motion.div>
      </section>
      </div>
    </>
  );
};

export default DevOps;
